import { ApolloLink, Observable, FetchResult, Operation } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';

import { clearTokens, getAccessToken, getRefreshToken, setTokens } from './storage';

type PendingRequest = { retry: () => void; abort: (err: Error) => void };

let isRefreshing = false;
let pendingRequests: PendingRequest[] = [];

function resolvePendingRequests(): void {
    pendingRequests.forEach(({ retry }) => retry());
    pendingRequests = [];
}

// Reject queued requests so their observer errors out instead of hanging forever.
// Without this, consumers (e.g. useQuery hooks) would stay in loading state when
// the refresh fails between an in-flight retry and the redirect.
function rejectPendingRequests(reason: Error): void {
    pendingRequests.forEach(({ abort }) => abort(reason));
    pendingRequests = [];
}

type Tokens = { accessToken: string; refreshToken: string };

// Serialize refresh across tabs/windows of this origin via Web Locks API.
// Without this, two tabs waking from sleep both POST /auth/refresh with the same
// refresh token — one rotates successfully, the other gets 401 and logs the user out.
async function fetchNewTokens(apiUrl: string): Promise<Tokens | null> {
    const initialRefreshToken = getRefreshToken();
    if (!initialRefreshToken) return null;

    const runRefresh = async (): Promise<Tokens | null> => {
        // Re-read after acquiring the lock: another tab may have rotated while we waited.
        const currentRefreshToken = getRefreshToken();
        if (!currentRefreshToken) return null;
        if (currentRefreshToken !== initialRefreshToken) {
            const accessToken = getAccessToken();
            if (!accessToken) return null;
            return { accessToken, refreshToken: currentRefreshToken };
        }

        const response = await fetch(`${apiUrl}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken: currentRefreshToken }),
        });

        if (!response.ok) {
            // Safety net: if storage rotated between request send and response,
            // another tab succeeded — use its tokens instead of forcing logout.
            const storedRefresh = getRefreshToken();
            const storedAccess = getAccessToken();
            if (storedRefresh && storedRefresh !== currentRefreshToken && storedAccess) {
                return { accessToken: storedAccess, refreshToken: storedRefresh };
            }
            return null;
        }

        const data = await response.json();
        return {
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
        };
    };

    if (typeof navigator !== 'undefined' && navigator.locks?.request) {
        return navigator.locks.request('totem-token-refresh', runRefresh);
    }
    return runRefresh();
}

function isSubscriptionOperation(operation: Operation): boolean {
    const definition = getMainDefinition(operation.query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
}

// HTTP requests get AHEEX ("Token expiré") when the access token expires, but subscriptions
// can't: the WS server hardcodes isTokenExpired=false, so a stale WS token surfaces as AHERA
// ("Authentification requise") instead. Both mean the same thing here — refresh the access
// token, which terminates and reconnects the socket (onTokenRefreshed) so graphql-ws re-sends
// connectionParams with the fresh token. AHERA on a query/mutation is a genuine "not
// authenticated" and is handled (redirect to login) by the error link, not here.
function needsTokenRefresh(result: FetchResult, operation: Operation): boolean {
    const errors = result.errors;
    if (!errors?.length) return false;

    const hasExpiredToken = errors.some((error) => (error.extensions?.code as string) === 'AHEEX');
    if (hasExpiredToken) return true;

    const hasUnauthenticated = errors.some(
        (error) => (error.extensions?.code as string) === 'AHERA' || error.message === 'Authentification requise',
    );
    return hasUnauthenticated && isSubscriptionOperation(operation);
}

function redirectToLogin(): void {
    clearTokens();
    window.location.href = '/login';
}

export function createTokenRefreshLink(apiUrl: string, onTokenRefreshed?: () => void): ApolloLink {
    return new ApolloLink((operation, forward) => {
        return new Observable<FetchResult>((observer) => {
            let isHandlingRefresh = false;

            forward(operation).subscribe({
                next: (result) => {
                    if (!needsTokenRefresh(result, operation)) {
                        observer.next(result);
                        return;
                    }

                    isHandlingRefresh = true;

                    if (isRefreshing) {
                        pendingRequests.push({
                            retry: () => {
                                const token = getAccessToken() || '';
                                operation.setContext(({ headers = {} }: { headers: Record<string, string> }) => ({
                                    headers: { ...headers, authorization: token },
                                }));
                                forward(operation).subscribe(observer);
                            },
                            abort: (err) => observer.error(err),
                        });
                        return;
                    }

                    isRefreshing = true;

                    fetchNewTokens(apiUrl)
                        .then((tokens) => {
                            if (!tokens) {
                                rejectPendingRequests(new Error('Session expired'));
                                redirectToLogin();
                                return;
                            }

                            setTokens(tokens.accessToken, tokens.refreshToken);
                            // Force WebSocket reconnection with the fresh token
                            onTokenRefreshed?.();

                            operation.setContext(({ headers = {} }: { headers: Record<string, string> }) => ({
                                headers: { ...headers, authorization: tokens.accessToken },
                            }));

                            resolvePendingRequests();
                            forward(operation).subscribe(observer);
                        })
                        .catch((err) => {
                            rejectPendingRequests(err instanceof Error ? err : new Error('Token refresh failed'));
                            redirectToLogin();
                        })
                        .finally(() => {
                            isRefreshing = false;
                        });
                },
                error: (error) => observer.error(error),
                complete: () => {
                    if (!isHandlingRefresh) {
                        observer.complete();
                    }
                },
            });
        });
    });
}
